/**
 * The way down.
 *
 * A slide is a moulded plastic tube coming out of a wall that has no reason to
 * have one, and it is the one prop in this game allowed to shout. Everything
 * else in `propviews.ts` is built to be recognised at two metres; this has to be
 * recognised at twenty, through fog, by somebody who has been walking for ten
 * minutes and wants out.
 *
 * ## What a Backrooms slide is
 *
 * This used to be an industrial refuse chute — steel, hazard stripes, a printed
 * DOWN placard — which is a fine object and the wrong one. The slides people
 * mean when they say "Backrooms slides" are documented across the wikis and the
 * games that popularised them, and they agree on the parts that matter:
 *
 *  - **Plastic tubes, in playground colours.** Blue, red, green and yellow are
 *    the four the Escape the Backrooms wiki lists, and four is small enough to
 *    be a fact rather than a palette.
 *  - **They jut out of walls with no discernible reason or explanation for how
 *    they were built.** That is the whole of the horror and it is architectural,
 *    not decorative: there is no frame, no lintel, no bolts and no bracket. The
 *    wall is simply broken and the tube is through it.
 *  - **They are anomalous, and most of them go to Level Fun.** The standing
 *    theory on the wikis is that the Partygoers put them there to draw people
 *    in. `SLIDE_ROUTES` in `world/levels.ts` already sends every slide in this
 *    game to the Funrooms or to Motion, and the surge in `entities/surge.ts`
 *    already has the building notice you standing at one, so the lore and the
 *    mechanic were pointing the same way before either knew about the other.
 *
 * Where the wikis differ is in whether a slide is an entrance you climb into or
 * an exit you are deposited from. This model takes the entrance reading,
 * because that is what the game needs it to be and because it is the one the
 * best-known source (the tube slides at the end of Level Fun, which the player
 * enters) describes.
 *
 * ## What that costs, and how it is paid for
 *
 * The old model carried its own signage and its conspicuity came from a bright
 * hazard band at eye level. A tube has no business carrying signage, so the band
 * is gone and something else has to reach fifteen metres:
 *
 *  - **The lip.** A 1.76 m flared collar of pale, chalked, scuffed plastic
 *    ringing a black hole at chest height. It is the largest single-coloured
 *    shape on any level and it is a *circle*, which nothing else in this game
 *    is — everything else is boxes. Silhouette does the work the printing used
 *    to.
 *  - **The colour.** Saturated, barely tinted toward the level (`belong` with
 *    almost no pull) and carrying its own emissive, so it holds in an unlit
 *    room. Which of the four canonical colours a level shows is chosen by
 *    contrast against that level's own walls — see `pickColours`. All four
 *    exist; a level shows the three that are wrong for it.
 *  - **Placement and light, which were always the real answer.** `lightSlides`
 *    in `world/gen/lighting.ts` puts three working fixtures over every mouth and
 *    `world/gen/slides.ts` picks the wall. That has not changed and this model
 *    must not undo it.
 *
 * ## The hole is a hole
 *
 * The throat is `MeshBasicMaterial` — no light reaches it — so shining a torch
 * in shows exactly what not shining one shows. It starts a little way inside the
 * mouth, so the first thirty centimetres are visibly plastic and everything past
 * that is not: the tube narrows and goes out. There is nowhere deeper for it to
 * go and nothing deeper to see.
 *
 * The dimensions are a contract with `PROP_DEFS.slide` in
 * `world/gen/propTables.ts` — 1.9 m across, 2.5 m deep, 2.2 m tall, centred on
 * its own origin in XZ, backed against a wall with local -Z facing the room.
 * Anything drawn outside that box is an object the player walks into the middle
 * of; anything short of it is an invisible wall. The tube runs the full 2.5 m
 * from the wall to the front face for exactly that reason, and the wreckage
 * fills the bottom of the box under it.
 */

import * as THREE from 'three';
import type { LevelSpec } from '../core/types';
import { makeRNG } from '../core/rng';
import { TAU } from '../core/math';
import { makeTapeAtlas, TAPE_STRIPES, type DecalAtlas } from './propdecals';
import { makePlasticGrime } from './propdreadmarks';
import {
  belong,
  box,
  css,
  makeShape,
  matte,
  merge,
  mergeUV,
  propPalette,
  remapUV,
  type PropPalette,
  type PropShape,
} from './propkit';

/* ------------------------------------------------------------------ *
 * Dimensions
 * ------------------------------------------------------------------ */

/** Half-extents of `PROP_DEFS.slide`. Nothing below may reach past these. */
const HALF_X = 0.95;
const HALF_Z = 1.25;

/** Outer radius of the tube, and the flared collar at its mouth. */
const TUBE_R = 0.7;
const LIP_R = 0.88;

/**
 * The axis, mouth first. The mouth end sits a touch higher than the wall end —
 * a four-degree droop, which is the only thing on the object that says which way
 * it goes. Anything steeper reads as a chute and starts asking to be modelled
 * with a floor in it.
 */
const MOUTH: readonly [number, number, number] = [0, 1.06, -(HALF_Z - 0.12)];
const WALL: readonly [number, number, number] = [0, 0.92, HALF_Z - 0.05];

/** How far along the tube the light stops, as a fraction of its length. */
const THROAT_START = 0.24;
/** Radius the throat has closed to by the far end. */
const THROAT_END_R = 0.19;

/** Where the moulded hoops sit, as fractions along the axis. */
const RIBS: readonly number[] = [0.3, 0.56, 0.82];
/** Half the axial width of one hoop. */
const RIB_HALF = 0.045;

/* ------------------------------------------------------------------ *
 * Axis helpers
 * ------------------------------------------------------------------ */

const _up = new THREE.Vector3(0, 1, 0);
const _fwd = new THREE.Vector3(0, 0, 1);
const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _quat = new THREE.Quaternion();

/** A point at fraction `s` along the tube, mouth to wall. */
function along(s: number): [number, number, number] {
  return [
    MOUTH[0] + (WALL[0] - MOUTH[0]) * s,
    MOUTH[1] + (WALL[1] - MOUTH[1]) * s,
    MOUTH[2] + (WALL[2] - MOUTH[2]) * s,
  ];
}

/**
 * A capless cone between two points, near radius first.
 *
 * `propkit.strut` is this with one radius; the tube's throat and its flared
 * collar both need two, and a cone is the cheapest shape that says "narrowing"
 * without anybody having to model a narrowing.
 */
function cone(
  rNear: number,
  rFar: number,
  from: readonly [number, number, number],
  to: readonly [number, number, number],
  segments: number
): THREE.BufferGeometry {
  _a.set(from[0], from[1], from[2]);
  _b.set(to[0], to[1], to[2]);
  _dir.subVectors(_b, _a);
  const length = _dir.length() || 1e-4;
  _dir.multiplyScalar(1 / length);
  // Three's cylinder puts `radiusTop` at +Y, and the quaternion below sends +Y
  // to `to` — so the far radius is the top one.
  const g = new THREE.CylinderGeometry(rFar, rNear, length, segments, 1, true);
  _quat.setFromUnitVectors(_up, _dir);
  g.applyQuaternion(_quat);
  g.translate((from[0] + to[0]) / 2, (from[1] + to[1]) / 2, (from[2] + to[2]) / 2);
  return g;
}

/** A flat disc or annulus square to the axis, facing the room. */
function facing(geo: THREE.BufferGeometry, at: readonly [number, number, number]): THREE.BufferGeometry {
  _dir.set(MOUTH[0] - WALL[0], MOUTH[1] - WALL[1], MOUTH[2] - WALL[2]).normalize();
  _quat.setFromUnitVectors(_fwd, _dir);
  geo.applyQuaternion(_quat);
  geo.translate(at[0], at[1], at[2]);
  return geo;
}

/**
 * Squeeze a part's v range into the slice of the tube it actually occupies.
 *
 * The grime map is laid out along the tube — clean and handled at the mouth,
 * dark at the far end — so a hoop eight centimetres wide that kept its own 0..1
 * v would show the entire gradient across itself and read as a painted black
 * band. See `makePlasticGrime`.
 */
function narrowV(geo: THREE.BufferGeometry, v0: number, v1: number): THREE.BufferGeometry {
  const uv = geo.attributes.uv as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) uv.setY(i, v0 + (v1 - v0) * uv.getY(i));
  uv.needsUpdate = true;
  return geo;
}

/** A box with a lean on it, for rubble that did not land square. */
function chunk(
  w: number,
  h: number,
  d: number,
  x: number,
  y: number,
  z: number,
  rx: number,
  ry: number,
  rz: number
): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, h, d);
  g.rotateY(ry);
  g.rotateX(rx);
  g.rotateZ(rz);
  g.translate(x, y, z);
  return g;
}

/* ------------------------------------------------------------------ *
 * Parts
 * ------------------------------------------------------------------ */

/**
 * The tube: a barrel and three moulded hoops, in one buffer.
 *
 * Double-sided at the material, so the inside of the barrel is the inside of
 * the barrel rather than a hole you can see the room through. The hoops are
 * what stop a 2.4 m cylinder reading as a pipe — a pipe is extruded and a slide
 * is moulded, and the difference is visible at ten metres in the way the
 * highlight breaks up.
 */
function tubeShell(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [cone(TUBE_R, TUBE_R, MOUTH, WALL, 20)];
  for (const s of RIBS) {
    const v0 = Math.max(0, s - RIB_HALF);
    const v1 = Math.min(1, s + RIB_HALF);
    parts.push(narrowV(cone(TUBE_R + 0.042, TUBE_R + 0.042, along(v0), along(v1), 20), v0, v1));
  }
  return mergeUV(parts);
}

/**
 * The lip: a flared collar and the flat ring around the opening.
 *
 * The one part of this a person puts their weight on, so the one part that has
 * gone pale — moulded plastic chalks where it is worn, and every hand and heel
 * that has gone over this edge has worn it. It is also, at 1.76 m across, the
 * thing that has to be visible from the far side of a room the player has not
 * entered yet.
 */
function tubeLip(): THREE.BufferGeometry {
  const flare = cone(LIP_R, TUBE_R + 0.03, MOUTH, along(0.09), 20);
  const face = facing(new THREE.RingGeometry(TUBE_R - 0.06, LIP_R, 24, 1), MOUTH);
  return merge([flare, face]);
}

/**
 * Where the light stops.
 *
 * A cone narrowing to a disc a little under two metres in. Unlit and near
 * black, so what the player gets is an outline of nothing, bounded by plastic,
 * that recedes as they walk round it. The floor mesh is unbroken and there is
 * a wall behind this; the depth is entirely the cone's.
 */
function tubeThroat(): THREE.BufferGeometry {
  const start = along(THROAT_START);
  const end = along(0.99);
  return merge([
    cone(TUBE_R - 0.03, THROAT_END_R, start, end, 16),
    facing(new THREE.CircleGeometry(THROAT_END_R, 16), end),
  ]);
}

/**
 * The wall it came through, and what came out of the wall.
 *
 * No frame and no fixings: there is a ragged collar of broken plaster where the
 * tube meets the wall, and the rest of that wall is on the floor underneath it.
 * This is the part that says the tube was not installed — and it is also what
 * makes the collision box honest, because the box is 2.5 m deep and a tube
 * hanging in mid-air over clear carpet would have the player walking into
 * nothing.
 *
 * `step` leans one big flat piece of the wall against the underside of the
 * mouth. Somebody put it there to get up, which means somebody got up.
 */
function wallWreck(seed: number, step: boolean): THREE.BufferGeometry {
  const rng = makeRNG(seed);
  const parts: THREE.BufferGeometry[] = [];

  // The broken edge, ringing the tube where it comes through the wall. Every
  // radius and half-extent below is chosen so that the *rotated* corner of the
  // largest chunk still lands inside HALF_X: 0.80 + 0.145 = 0.945. A ring that
  // overhangs the footprint is an object the player can see and never reach,
  // which is the same lie as an invisible wall told the other way round.
  for (let i = 0; i < 15; i++) {
    const a = (i / 15) * TAU + rng.range(-0.12, 0.12);
    const r = rng.range(0.72, 0.8);
    parts.push(
      chunk(
        rng.range(0.1, 0.18),
        rng.range(0.1, 0.2),
        rng.range(0.05, 0.11),
        Math.cos(a) * r,
        WALL[1] + Math.sin(a) * r,
        WALL[2] - rng.range(0.1, 0.2),
        rng.range(-0.3, 0.3),
        rng.range(-0.4, 0.4),
        a + rng.range(-0.4, 0.4)
      )
    );
  }

  // What fell out of it. Flat, because plasterboard breaks flat, and spread
  // across the whole footprint so the bottom of the box is occupied.
  for (let i = 0; i < 14; i++) {
    parts.push(
      chunk(
        rng.range(0.12, 0.4),
        rng.range(0.025, 0.07),
        rng.range(0.1, 0.34),
        rng.range(-0.62, 0.62),
        rng.range(0.02, 0.09),
        rng.range(-0.92, 0.92),
        rng.range(-0.16, 0.16),
        rng.range(0, TAU),
        rng.range(-0.16, 0.16)
      )
    );
  }
  // Dust, banked against the wall where it always ends up.
  parts.push(chunk(1.6, 0.05, 0.34, 0, 0.025, WALL[2] - 0.24, 0, 0, 0));

  if (step) {
    // Leaned against the tube at about forty degrees, its foot out in the room.
    // Wide enough to stand on and thin enough to have come off a wall.
    parts.push(chunk(0.66, 0.05, 0.95, -0.42, 0.31, -0.62, -0.72, 0.18, 0.06));
    parts.push(chunk(0.3, 0.04, 0.24, -0.5, 0.04, -1.04, 0.05, 0.4, 0));
  }

  return merge(parts);
}

/**
 * Somebody strung a length of tape across it.
 *
 * Kept from the model this replaced, because it is the one thing on the object
 * that is unambiguously a person's rather than the building's: whoever put it
 * up had decided this ought to be stopped and had one roll of tape to do it
 * with. It is already half down.
 */
function mouthTape(atlas: DecalAtlas): THREE.BufferGeometry {
  const stripes = atlas.entries[TAPE_STRIPES]!;
  const z = MOUTH[2] - 0.04;
  const parts: THREE.BufferGeometry[] = [box(1.66, 0.06, 0.012, 0, MOUTH[1] + 0.34, z)];

  const slack = box(1.5, 0.055, 0.012);
  slack.rotateZ(-0.36);
  slack.translate(-0.1, MOUTH[1] - 0.22, z);
  parts.push(slack);

  const loose = box(0.62, 0.05, 0.012);
  loose.rotateZ(1.05);
  loose.translate(0.62, MOUTH[1] - 0.02, z);
  parts.push(loose);

  for (const g of parts) remapUV(g, stripes);
  return mergeUV(parts);
}

/* ------------------------------------------------------------------ *
 * Colour
 * ------------------------------------------------------------------ */

/**
 * The four colours the wikis list, in the order they list them.
 *
 * Saturated well past anything the building owns. These are not level colours
 * and they are not pulled toward one — a slide belongs to the Backrooms rather
 * than to the room it is in, which is the same reasoning `PropPalette.carton`
 * uses for the things a person carried in, taken further because nobody carried
 * this in either.
 */
const SLIDE_COLOURS: readonly number[] = [0x2f5fb8, 0xb8382f, 0x2f8f4a, 0xd8b224];

/** How many of them one level shows. */
const SLIDE_VARIANTS = 3;

/**
 * The three of the four this level gets, most-wrong first.
 *
 * A yellow tube on Level 0 is canon and is also invisible, and findability is
 * not negotiable — so the four are ranked by how far each sits from the wall,
 * the floor and the air of the level it would be standing in, and the closest
 * one is dropped. On the Lobby that drops yellow; in the Poolrooms it drops
 * blue; in the Funrooms, whose walls are yellow and whose floor is blue, it
 * drops whichever of those two the room leans on harder. Every colour still
 * appears somewhere in a run.
 */
function pickColours(spec: LevelSpec): number[] {
  const against = [
    new THREE.Color(spec.palette.wall),
    new THREE.Color(spec.palette.carpet),
    new THREE.Color(spec.palette.fog),
  ];
  const scored = SLIDE_COLOURS.map((hex) => {
    const c = new THREE.Color(hex);
    let worst = Infinity;
    for (const other of against) {
      const d = Math.hypot(c.r - other.r, c.g - other.g, c.b - other.b);
      if (d < worst) worst = d;
    }
    return { hex, worst };
  });
  scored.sort((a, b) => b.worst - a.worst);
  return scored.slice(0, SLIDE_VARIANTS).map((s) => s.hex);
}

/** Plastic: barely tinted, self-lit enough to hold its colour in an unlit room. */
function plasticMaterial(hex: number, pal: PropPalette, grime: THREE.Texture): THREE.Material {
  const color = belong(hex, pal, pal.pull * 0.12, 1);
  return new THREE.MeshStandardMaterial({
    color,
    map: grime,
    emissive: color,
    emissiveIntensity: 0.15,
    roughness: 0.52,
    metalness: 0,
    side: THREE.DoubleSide,
  });
}

/* ------------------------------------------------------------------ *
 * The kind
 * ------------------------------------------------------------------ */

/**
 * Build the slide bundle for one level.
 *
 * Three variants, which are three colours and three states of the same thing:
 * one with the wall leaned against it to climb up on, one with nothing, and one
 * taped off by somebody who came through before. Colour and state are baked
 * together rather than crossed, because the shape is what the player reads and
 * the difference between six variants and three is six meshes an instance.
 */
export function buildSlide(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const colours = pickColours(spec);

  // One grime map for all three colours: it is a multiply, so the same dirt
  // sits on a red tube and a blue one. The first variant is the one with the
  // step under it, so it is the one that has been handled most.
  const grime = makePlasticGrime(0x51d3, 11);

  // Chalked, scuffed and pale. Deliberately lighter than anything the level
  // owns, for the same reason the old model's steel was: a flashlight crossing a
  // dark room has to catch this before it catches the wallpaper behind it.
  const lipColor = belong(0xdedbd2, pal, pal.pull * 0.25, 1.06);
  const lip = new THREE.MeshStandardMaterial({
    color: lipColor,
    emissive: lipColor,
    emissiveIntensity: 0.13,
    roughness: 0.66,
    metalness: 0,
    side: THREE.DoubleSide,
  });

  // The wall's own substrate, which nobody in this building was ever meant to
  // see. Paler and deader than the wallpaper over it.
  const plaster = matte(belong(0xcfc9ba, pal, pal.pull * 0.55, pal.gain), 0.04);

  // Unlit, double sided: the inside of the throat is what is being looked at,
  // and no torch is going to change what is down there.
  const nothing = new THREE.MeshBasicMaterial({ color: 0x05060a, side: THREE.DoubleSide });

  const tapeAtlas = makeTapeAtlas(
    css(belong(0xd7b02b, pal, pal.pull * 0.28, 1)),
    css(belong(0x191917, pal, pal.pull * 0.3, 1))
  );
  const tape = new THREE.MeshLambertMaterial({
    map: tapeAtlas.texture,
    emissive: 0xffffff,
    emissiveMap: tapeAtlas.texture,
    emissiveIntensity: 0.14,
  });

  const shell = tubeShell();
  const throat = tubeThroat();
  const lipGeo = tubeLip();

  return makeShape(
    spec,
    // No yaw jitter and no tilt: `PropIndex` turns the collision box by
    // `PropSpot.yaw` alone, and this is the one prop whose box the player will
    // walk straight at. It also came through the wall, so it has no business
    // leaning relative to the wall.
    { kind: 'floor' },
    [
      // One layer per colour, since colour lives on the material. Two of the
      // three meshes on any given instance are hidden.
      ...colours.map((hex, i) => ({
        material: plasticMaterial(hex, pal, grime),
        variants: colours.map((_, j) => (i === j ? shell : null)),
      })),
      { material: lip, variants: [lipGeo, lipGeo, lipGeo] },
      {
        material: plaster,
        variants: [wallWreck(0x1a77, true), wallWreck(0x2b31, false), wallWreck(0x3c05, false)],
      },
      { material: nothing, variants: [throat, throat, throat] },
      { material: tape, variants: [null, null, mouthTape(tapeAtlas)] },
    ],
    [tapeAtlas, grime]
  );
}
